import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Alert,
  CircularProgress,
  Avatar,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Category as CategoryIcon,
  Article as ArticleIcon,
  Image as ImageIcon,
  Link as LinkIcon,
  Schedule as TimeIcon,
  TrendingUp as DifficultyIcon,
  Person as PersonIcon,
  Comment as CommentIcon,
  ThumbUp as ThumbUpIcon,
  CalendarToday as CalendarIcon,
  PushPin as PinIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { FaultCategory, FaultTopic } from '../../types/maintenanceBank';
import { FaultCategoryService, FaultTopicService } from '../../services/maintenanceBankService';
import AddTopicDialog from '../../components/AddTopicDialog';

const FaultsPage: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<FaultCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<FaultCategory | null>(null);
  const [topics, setTopics] = useState<FaultTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // حوارات
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showTopicDialog, setShowTopicDialog] = useState(false);
  const [showAddTopicDialog, setShowAddTopicDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FaultCategory | null>(null);
  const [editingTopic, setEditingTopic] = useState<FaultTopic | null>(null);

  // بيانات النماذج
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    color: '#1976d2',
    icon: '🔧'
  });

  const [topicForm, setTopicForm] = useState({
    title: '',
    description: '',
    content: '',
    difficulty: 'medium' as 'easy' | 'medium' | 'hard',
    estimatedTime: 30,
    tags: '',
    images: [] as string[]
  });

  // تحميل الفئات عند بدء الصفحة
  useEffect(() => {
    console.log('🚀 تهيئة صفحة الأعطال...');
    loadCategories();
  }, []);

  // تحميل المواضيع عند اختيار فئة
  useEffect(() => {
    if (selectedCategory) {
      console.log(`🔄 تغيير الفئة المختارة إلى: ${selectedCategory.name} (${selectedCategory.id})`);
      loadTopics(selectedCategory.id);
    } else {
      console.log('⚠️ لا توجد فئة مختارة');
      setTopics([]);
    }
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 بدء تحميل الفئات...');

      const categoriesData = await FaultCategoryService.getCategories();
      console.log(`✅ تم تحميل ${categoriesData.length} فئة:`, categoriesData.map(c => ({ id: c.id, name: c.name })));

      setCategories(categoriesData);
      if (categoriesData.length > 0 && !selectedCategory) {
        setSelectedCategory(categoriesData[0]);
        console.log(`📌 تم اختيار الفئة الافتراضية: ${categoriesData[0].name} (${categoriesData[0].id})`);
      } else if (categoriesData.length === 0) {
        console.log('⚠️ لا توجد فئات متاحة - سيتم إنشاء فئة افتراضية');
        try {
          // إنشاء فئة افتراضية
          const defaultCategory = {
            name: 'أعطال عامة',
            description: 'فئة افتراضية للأعطال العامة',
            icon: '🔧',
            color: '#2196f3',
            order: 0,
            isActive: true,
            branchId: '', // سيتم تعيينها من المستخدم الحالي
            createdBy: '' // سيتم تعيينها من المستخدم الحالي
          };

          const categoryId = await FaultCategoryService.addCategory(defaultCategory);
          console.log(`✅ تم إنشاء فئة افتراضية: ${categoryId}`);

          // إعادة تحميل الفئات
          await loadCategories();
        } catch (createError: any) {
          console.error('❌ فشل في إنشاء فئة افتراضية:', createError);
          setError('لا توجد فئات متاحة ولا يمكن إنشاء فئة افتراضية. يرجى التحقق من الأذونات.');
        }
      }
    } catch (error: any) {
      console.error('❌ خطأ في تحميل الفئات:', error);
      setError(`فشل في تحميل الفئات: ${error.message || 'خطأ غير معروف'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadTopics = async (categoryId: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log(`🔄 بدء تحميل المواضيع للفئة: ${categoryId}`);

      // استخدام getAllTopicsByCategory لجلب جميع المواضيع (منشورة وغير منشورة)
      const result = await FaultTopicService.getAllTopicsByCategory(categoryId);
      setTopics(result.items);
      console.log(`✅ تم تحميل ${result.items.length} موضوع للفئة ${categoryId}`);

      if (result.items.length === 0) {
        console.log(`ℹ️ لا توجد مواضيع في الفئة ${categoryId}`);
      }
    } catch (error: any) {
      console.error('❌ خطأ مفصل في تحميل المواضيع:', {
        error: error,
        message: error.message,
        code: error.code,
        categoryId: categoryId
      });
      setError(`فشل في جلب المواضيع: ${error.message || error.code || 'خطأ غير معروف'}`);
    } finally {
      setLoading(false);
    }
  };

  // معالجة نجاح إضافة موضوع جديد
  const handleTopicAdded = (newTopic: FaultTopic) => {
    // إضافة الموضوع الجديد للقائمة
    setTopics(prevTopics => [newTopic, ...prevTopics]);

    // إذا كان الموضوع في فئة مختلفة، انتقل إليها
    if (newTopic.categoryId !== selectedCategory?.id) {
      const targetCategory = categories.find(cat => cat.id === newTopic.categoryId);
      if (targetCategory) {
        setSelectedCategory(targetCategory);
      }
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      description: '',
      color: '#1976d2',
      icon: '🔧'
    });
    setShowCategoryDialog(true);
  };

  const handleEditCategory = (category: FaultCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      color: category.color || '#1976d2',
      icon: category.icon || '🔧'
    });
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    try {
      const categoryData = {
        ...categoryForm,
        order: editingCategory?.order || categories.length,
        createdBy: 'current-user', // يجب استبداله بالمستخدم الحالي
        branchId: 'current-branch' // يجب استبداله بالفرع الحالي
      };

      if (editingCategory) {
        await FaultCategoryService.updateCategory(editingCategory.id, categoryData);
      } else {
        await FaultCategoryService.addCategory(categoryData);
      }

      setShowCategoryDialog(false);
      loadCategories();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDeleteCategory = async (category: FaultCategory) => {
    if (window.confirm(`هل أنت متأكد من حذف فئة "${category.name}"؟`)) {
      try {
        await FaultCategoryService.deleteCategory(category.id);
        loadCategories();
        if (selectedCategory?.id === category.id) {
          setSelectedCategory(null);
          setTopics([]);
        }
      } catch (error: any) {
        setError(error.message);
      }
    }
  };

  const handleAddTopic = () => {
    if (!selectedCategory) return;
    setShowAddTopicDialog(true);
  };

  // عرض موضوع
  const handleViewTopic = (topicId: string) => {
    navigate(`/admin/maintenance-bank/topic/${topicId}`);
  };

  // تعديل موضوع
  const handleEditTopic = (topicId: string) => {
    navigate(`/admin/maintenance-bank/topic/${topicId}/edit`);
  };

  // حذف موضوع
  const handleDeleteTopic = async (topicId: string, topicTitle: string) => {
    if (window.confirm(`هل أنت متأكد من حذف الموضوع "${topicTitle}"؟`)) {
      try {
        await FaultTopicService.deleteTopic(topicId);

        // إعادة تحميل المواضيع
        if (selectedCategory) {
          await loadTopics(selectedCategory.id);
        }

        console.log('✅ تم حذف الموضوع بنجاح');
      } catch (error: any) {
        console.error('❌ خطأ في حذف الموضوع:', error);
        alert(`فشل في حذف الموضوع: ${error.message}`);
      }
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'medium': return 'warning';
      case 'hard': return 'error';
      default: return 'default';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'سهل';
      case 'medium': return 'متوسط';
      case 'hard': return 'صعب';
      default: return 'غير محدد';
    }
  };

  if (loading && categories.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* قائمة الفئات */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                <CategoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                فئات الأعطال
              </Typography>
              <IconButton onClick={handleAddCategory} color="primary">
                <AddIcon />
              </IconButton>
            </Box>

            {categories.map((category) => (
              <Card
                key={category.id}
                sx={{
                  mb: 1,
                  cursor: 'pointer',
                  border: selectedCategory?.id === category.id ? 2 : 1,
                  borderColor: selectedCategory?.id === category.id ? 'primary.main' : 'divider',
                  '&:hover': {
                    boxShadow: 2
                  }
                }}
                onClick={() => setSelectedCategory(category)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" mb={1}>
                    <Avatar
                      sx={{
                        bgcolor: category.color || 'primary.main',
                        width: 32,
                        height: 32,
                        mr: 1,
                        fontSize: '1rem'
                      }}
                    >
                      {category.icon || '🔧'}
                    </Avatar>
                    <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                      {category.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditCategory(category);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(category);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {category.description && (
                    <Typography variant="caption" color="text.secondary">
                      {category.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Paper>
        </Grid>

        {/* قائمة المواضيع */}
        <Grid item xs={12} md={9}>
          {selectedCategory ? (
            <Paper sx={{ p: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6">
                  <ArticleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  مواضيع فئة: {selectedCategory.name}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddTopic}
                >
                  إضافة موضوع
                </Button>
              </Box>

              {/* عرض المواضيع على شكل منتدى */}
              <Box>
                {topics.map((topic, index) => (
                  <Paper
                    key={topic.id}
                    sx={{
                      mb: 1,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: 3,
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        p: 2,
                        gap: 2,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleViewTopic(topic.id)}
                    >
                      {/* صورة الموضوع */}
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          flexShrink: 0,
                          borderRadius: 1,
                          overflow: 'hidden',
                          bgcolor: 'grey.100'
                        }}
                      >
                        {topic.images && topic.images.length > 0 ? (
                          <img
                            src={topic.images[0]}
                            alt={topic.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'grey.400'
                            }}
                          >
                            <ImageIcon sx={{ fontSize: 40 }} />
                          </Box>
                        )}
                      </Box>

                      {/* معلومات الموضوع */}
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        {/* العنوان والحالة */}
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          {!topic.isPublished && (
                            <PinIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                          )}
                          <Typography
                            variant="h6"
                            sx={{
                              fontSize: '1.1rem',
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flexGrow: 1
                            }}
                          >
                            {topic.title}
                          </Typography>
                          {!topic.isPublished && (
                            <Chip
                              label="مسودة"
                              size="small"
                              color="warning"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>

                        {/* الوصف */}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {topic.description || 'لا يوجد وصف'}
                        </Typography>

                        {/* المعلومات الإضافية */}
                        <Box display="flex" flexWrap="wrap" gap={1} alignItems="center">
                          <Chip
                            icon={<DifficultyIcon />}
                            label={getDifficultyText(topic.difficulty)}
                            color={getDifficultyColor(topic.difficulty) as any}
                            size="small"
                            sx={{ height: 24 }}
                          />
                          
                          {topic.estimatedTime && (
                            <Chip
                              icon={<TimeIcon />}
                              label={`${topic.estimatedTime} د`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 24 }}
                            />
                          )}

                          {topic.images && topic.images.length > 1 && (
                            <Chip
                              icon={<ImageIcon />}
                              label={`${topic.images.length} صور`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 24 }}
                            />
                          )}

                          {topic.tags && topic.tags.length > 0 && (
                            <>
                              {topic.tags.slice(0, 2).map((tag, tagIndex) => (
                                <Chip
                                  key={tagIndex}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 24, fontSize: '0.7rem' }}
                                />
                              ))}
                              {topic.tags.length > 2 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{topic.tags.length - 2}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>

                      {/* الإحصائيات والأزرار */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          minWidth: 120
                        }}
                      >
                        {/* الإحصائيات */}
                        <Box display="flex" gap={2} mb={1}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <ViewIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {topic.views || 0}
                            </Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <ThumbUpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {topic.likes || 0}
                            </Typography>
                          </Box>
                        </Box>

                        {/* التاريخ */}
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                          {topic.createdAt?.toLocaleDateString('ar-EG')}
                        </Typography>

                        {/* الأزرار */}
                        <Box display="flex" gap={0.5}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTopic(topic.id);
                            }}
                            sx={{ bgcolor: 'action.hover' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTopic(topic.id, topic.title);
                            }}
                            sx={{ bgcolor: 'action.hover' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>

              {topics.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography variant="h6" color="text.secondary">
                    لا توجد مواضيع في هذه الفئة
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    انقر على "إضافة موضوع" لإضافة أول موضوع
                  </Typography>
                </Box>
              )}
            </Paper>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                اختر فئة لعرض المواضيع
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* حوار إضافة/تعديل فئة */}
      <Dialog open={showCategoryDialog} onClose={() => setShowCategoryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'تعديل الفئة' : 'إضافة فئة جديدة'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="اسم الفئة"
            value={categoryForm.name}
            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="الوصف"
            value={categoryForm.description}
            onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="الأيقونة (إيموجي)"
            value={categoryForm.icon}
            onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="اللون"
            type="color"
            value={categoryForm.color}
            onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCategoryDialog(false)}>إلغاء</Button>
          <Button onClick={handleSaveCategory} variant="contained">
            {editingCategory ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* حوار إضافة موضوع جديد */}
      <AddTopicDialog
        open={showAddTopicDialog}
        onClose={() => setShowAddTopicDialog(false)}
        onSuccess={handleTopicAdded}
        categories={categories}
        selectedCategoryId={selectedCategory?.id}
      />
    </Box>
  );
};

export default FaultsPage;
